// @ts-nocheck  — browser storage helpers (localStorage + file export/import).
// Bundled into app.js. Keeps the whole strategy (incl. custom blocks) between
// sessions, and lets you move it as a .json file.

const KEY = "tickahead_composable_strategy_v4"; // v4 = trade management as WHEN→THEN rules

const rid = () => "r_" + Math.random().toString(36).slice(2, 8);
/** Build a manage[] rule list from the old flat %/scale fields (back-compat). */
function migrateManage(st) {
  const m = [];
  if (st.stopLossPct > 0) m.push({ id: rid(), when: { kind: "loss", value: st.stopLossPct }, then: { kind: "closeAll" } });
  if (st.takeProfitPct > 0) m.push({ id: rid(), when: { kind: "profit", value: st.takeProfitPct }, then: { kind: "closeAll" } });
  if (Array.isArray(st.scaleOut)) for (const r of st.scaleOut) if (r.takePct > 0) m.push({ id: rid(), when: { kind: "profit", value: r.atPct }, then: { kind: "closePart", fraction: r.takePct / 100 } });
  if (st.breakevenPct > 0) m.push({ id: rid(), when: { kind: "profit", value: st.breakevenPct }, then: { kind: "breakeven" } });
  if (st.trailPct > 0) m.push({ id: rid(), when: { kind: "profit", value: st.trailPct }, then: { kind: "trail", value: st.trailPct } });
  if (st.scaleIn && st.scaleIn.maxAdds > 0) m.push({ id: rid(), when: { kind: "profit", value: st.scaleIn.everyPct }, then: { kind: "add", fraction: (st.scaleIn.addPct || 0) / 100, maxAdds: st.scaleIn.maxAdds } });
  return m;
}

/** Validate/repair a loaded strategy; reject incompatible tree shapes. */
function normalize(s) {
  if (!s || typeof s !== "object" || !Array.isArray(s.setups)) return null;
  if (typeof s.name !== "string" || !s.name) s.name = "Untitled strategy";
  s.blocks = Array.isArray(s.blocks) ? s.blocks : [];
  s.sizing = s.sizing ?? { mode: "fixed", size: 1, riskPct: 1 };
  s.rails = s.rails ?? { maxDailyLossPct: 0, maxDrawdownPct: 0, profitTargetPct: 0, maxTradesPerDay: 0, unit: "pct", accountSize: 100000 };
  // Discipline guards (D-089) — backfill on strategies saved before they existed.
  s.rails.dailyProfitTargetPct = s.rails.dailyProfitTargetPct ?? 0;
  s.rails.maxConsecutiveLosses = s.rails.maxConsecutiveLosses ?? 0;
  s.rails.lossCooldownMins = s.rails.lossCooldownMins ?? 0;
  if (s.rails.unit !== "usd") s.rails.unit = "pct";
  if (typeof s.rails.accountSize !== "number" || s.rails.accountSize <= 0) s.rails.accountSize = 100000;
  s.backtest = s.backtest ?? { costPct: 0, splitPct: 100, goal: "grow" };
  if (!Array.isArray(s.deployment)) s.deployment = [];
  for (const st of s.setups) {
    if (!st.entry || st.entry.kind !== "group" || !st.exit || st.exit.kind !== "group") return null;
    if (!st.order || typeof st.order !== "object") st.order = { type: "market", ref: "close", offsetPct: 0 };
    if (!Array.isArray(st.manage)) st.manage = migrateManage(st);
    for (const r of st.manage) if (!r.id) r.id = rid();
    delete st.scaleOut; delete st.scaleIn; delete st.stopLossPct; delete st.takeProfitPct; delete st.breakevenPct; delete st.trailPct;
  }
  return s;
}

// Per-security working strategy (D-082): each instrument's chart tab edits its
// OWN current strategy, TradingView-style — switch securities and you're back
// where you left off, independent of the others. The named library below stays
// global/shared (reusable methods; Deploy picks from it). A symbol-less call
// keeps the legacy global slot for back-compat.
function workingKey(symbol) { return symbol ? KEY + "::" + symbol : KEY; }

export function loadStrategy(symbol?) {
  try { const raw = localStorage.getItem(workingKey(symbol)); return raw ? normalize(JSON.parse(raw)) : null; }
  catch { return null; }
}
export function saveStrategy(s, symbol?) {
  try { localStorage.setItem(workingKey(symbol), JSON.stringify(s)); } catch { /* ignore (private mode / file://) */ }
}

export function exportStrategy(s) {
  const blob = new Blob([JSON.stringify(s, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "strategy.json"; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// --- named strategy library (save several, load any) ------------------------
const LIB = "tickahead_composable_library_v1";
function readLib() { try { return JSON.parse(localStorage.getItem(LIB) || "{}") || {}; } catch { return {}; } }
function writeLib(o) { try { localStorage.setItem(LIB, JSON.stringify(o)); } catch { /* ignore */ } }
export function listSavedStrategies() { return Object.keys(readLib()).sort(); }
export function saveNamedStrategy(name, s) { const lib = readLib(); lib[name] = JSON.parse(JSON.stringify(s)); writeLib(lib); }
export function loadNamedStrategy(name) { const s = readLib()[name]; return s ? normalize(JSON.parse(JSON.stringify(s))) : null; }
export function deleteNamedStrategy(name) { const lib = readLib(); delete lib[name]; writeLib(lib); }

export function importStrategy(cb) {
  const input = document.createElement("input");
  input.type = "file"; input.accept = "application/json,.json";
  input.addEventListener("change", () => {
    const file = input.files && input.files[0]; if (!file) return cb(null);
    const reader = new FileReader();
    reader.onload = () => { try { cb(normalize(JSON.parse(String(reader.result)))); } catch { cb(null); } };
    reader.readAsText(file);
  });
  input.click();
}
